"use client";

import { useCallback, useEffect, useState } from "react";
import { SafeImage } from "@/components/SafeImage";

// Stay photo gallery: a featured collage that opens a full-screen lightbox
// (prev/next, keyboard, counter) showing every photo. No external deps.
export function StayGallery({
  images,
  alt,
  showAllLabel,
  fallback,
}: {
  images: string[];
  alt: string;
  showAllLabel: string;
  fallback: string;
}) {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const count = images.length;

  const openAt = useCallback((i: number) => {
    setIndex(i);
    setOpen(true);
  }, []);
  const close = useCallback(() => setOpen(false), []);
  const prev = useCallback(
    () => setIndex((i) => (i - 1 + count) % count),
    [count],
  );
  const next = useCallback(() => setIndex((i) => (i + 1) % count), [count]);

  // Keyboard navigation + lock page scroll while the lightbox is open.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    }
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, close, prev, next]);

  if (count === 0) return null;

  // Collage: big cover + up to 4 thumbnails; the 4th shows "+N" if more remain.
  const thumbs = images.slice(1, 5);
  const hiddenCount = count - 5;

  return (
    <>
      <div className="relative">
        <div className="grid gap-3 sm:grid-cols-4 sm:grid-rows-2">
          <button
            type="button"
            onClick={() => openAt(0)}
            className="group relative aspect-[4/3] overflow-hidden rounded-2xl sm:col-span-2 sm:row-span-2 sm:aspect-auto"
          >
            <SafeImage
              src={images[0]}
              fallback={fallback}
              alt={alt}
              fill
              priority
              fetchPriority="high"
              sizes="(max-width: 768px) 100vw, 50vw"
              className="object-cover transition-transform duration-300 group-hover:scale-105"
            />
          </button>

          {thumbs.map((src, i) => {
            const realIndex = i + 1;
            const isLast = i === thumbs.length - 1;
            const showMore = isLast && hiddenCount > 0;
            return (
              <button
                type="button"
                key={src}
                onClick={() => openAt(realIndex)}
                className="group relative aspect-[4/3] overflow-hidden rounded-2xl sm:col-span-1"
              >
                <SafeImage
                  src={src}
                  fallback={fallback}
                  alt={`${alt} ${realIndex + 1}`}
                  fill
                  sizes="(max-width: 768px) 50vw, 25vw"
                  className="object-cover transition-transform duration-300 group-hover:scale-105"
                />
                {showMore && (
                  <span className="absolute inset-0 grid place-items-center bg-black/55 text-lg font-semibold text-white">
                    +{hiddenCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {count > 1 && (
          <button
            type="button"
            onClick={() => openAt(0)}
            className="absolute bottom-3 right-3 rounded-full bg-cream/90 px-4 py-2 text-sm font-medium text-bark shadow-sm backdrop-blur transition-colors hover:bg-cream"
          >
            ▦ {showAllLabel} ({count})
          </button>
        )}
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
          onClick={close}
          role="dialog"
          aria-modal="true"
        >
          {/* Close */}
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="absolute right-4 top-4 z-20 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-2xl text-white hover:bg-white/20"
          >
            ✕
          </button>

          {/* Counter */}
          <span className="absolute left-1/2 top-5 z-20 -translate-x-1/2 text-sm font-medium text-white/90">
            {index + 1} / {count}
          </span>

          {/* Prev */}
          {count > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                prev();
              }}
              aria-label="Previous"
              className="absolute left-3 top-1/2 z-20 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-3xl text-white hover:bg-white/20 sm:left-6"
            >
              ‹
            </button>
          )}

          {/* Image */}
          <div
            className="relative z-0 h-[80vh] w-[92vw] max-w-5xl"
            onClick={(e) => e.stopPropagation()}
          >
            <SafeImage
              src={images[index]}
              fallback={fallback}
              alt={`${alt} ${index + 1}`}
              fill
              sizes="92vw"
              className="object-contain"
            />
          </div>

          {/* Next */}
          {count > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                next();
              }}
              aria-label="Next"
              className="absolute right-3 top-1/2 z-20 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-3xl text-white hover:bg-white/20 sm:right-6"
            >
              ›
            </button>
          )}
        </div>
      )}
    </>
  );
}
